/**
 * 【演示数据工厂 —— 待后端接口就绪后移除】
 *
 * 后端目前只实现了 /api/auth/*，发票、校验、流程、档案等接口正在并行开发。
 * 这里按 @ilc/shared 的实体类型构造演示数据，让页面在没有后端的情况下也能完整走查。
 * 接口就绪后，删除本目录即可，页面代码无需改动（降级逻辑在 useResource 里）。
 *
 * 注意：演示数据必须与真实实体类型完全一致（TS 会强制校验），
 * 否则接口上线后页面会因为字段缺失而报错，掩盖真实问题。
 */

import {
  CheckStatus,
  ContractLevel,
  ContractStatus,
  ContractType,
  CONTRACT_LEVEL_OF,
  CounterpartyRole,
  DataScope,
  FlowInstanceStatus,
  FlowNodeType,
  FlowTaskStatus,
  InvoiceSource,
  InvoiceStatus,
  InvoiceTypeCode,
  InvoiceValidity,
  resolveInvoiceTaxRateText,
  IsolationMode,
  PlanTier,
  PrincipalType,
  ProviderCallStatus,
  ProviderCode,
  ProviderMode,
  RiskItemCode,
  RiskLevel,
  SettlementMethod,
  TenantStatus,
  TplusSyncStatus,
  UserStatus,
  BillingMode,
  BizType,
  DefaultRoleCode,
  RiskItems,
  type ArchiveListItem,
  type AuditLog,
  type AuthContext,
  type CheckReport,
  type Contract,
  type ContractRef,
  type Dept,
  type FlowInstance,
  type FlowTask,
  type FlowTemplate,
  type Invoice,
  type InvoiceCheck,
  type InvoiceDetail,
  type InvoiceListItem,
  type InvoiceOcr,
  type Message,
  type OperationLog,
  type PlatformMetrics,
  type Plan,
  type ProviderCallLog,
  type ProviderConfig,
  type Role,
  type SupplierCheck,
  type TaxCheck,
  type Tenant,
  type TenantConfig,
  type TenantListItem,
  type TenantMetrics,
  type TplusSync,
  type User,
} from '@ilc/shared';

export const DEMO_TENANT_ID = 'e721a4f8-7ba6-49cf-8092-7d0ebbfecbaa';
export const DEMO_TENANT_CODE = 'DEMO001';

/** 生成 ISO 时间字符串（相对今天偏移 n 天） */
export function iso(daysAgo = 0, hour = 10, minute = 30): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

export function taxDate(daysAgo = 0): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

const base = (id: string, daysAgo = 0) => ({
  id,
  tenantId: DEMO_TENANT_ID,
  createdAt: iso(daysAgo, 9, 12),
  updatedAt: iso(Math.max(0, daysAgo - 1), 16, 40),
});

/**
 * 演示发票 ID 映射
 *
 * 后端把发票 ID 当 UUID 校验：非 UUID 会直接返回 400「参数校验失败」，
 * 而 400 属于参数错误，前端**不降级**（避免用假数据掩盖真实问题）；
 * 只有合法 UUID 但查不到时，后端返回 404，前端才会切到本地演示数据。
 * 所以演示数据统一使用合法 UUID，短名只是为了在演示数据内部互相引用时更可读。
 */
const MOCK_INVOICE_ID_MAP: Record<string, string> = {
  'inv-001': '00000000-0000-4000-8000-000000000001',
  'inv-002': '00000000-0000-4000-8000-000000000002',
  'inv-003': '00000000-0000-4000-8000-000000000003',
  'inv-004': '00000000-0000-4000-8000-000000000004',
  'inv-005': '00000000-0000-4000-8000-000000000005',
  'inv-006': '00000000-0000-4000-8000-000000000006',
  'inv-007': '00000000-0000-4000-8000-000000000007',
  'inv-008': '00000000-0000-4000-8000-000000000008',
  'inv-009': '00000000-0000-4000-8000-000000000009',
  'inv-010': '00000000-0000-4000-8000-000000000010',
};

/** 短名 → UUID；已经是 UUID 的原样返回 */
export function mockInvoiceId(shortId: string): string {
  return MOCK_INVOICE_ID_MAP[shortId] ?? shortId;
}

/* ==========================================================================
 * 发票
 * ========================================================================== */

export interface InvoiceSeed {
  id: string;
  bizNo: string;
  invoiceType: InvoiceTypeCode;
  invoiceCode: string | null;
  invoiceNo: string;
  daysAgo: number;
  sellerName: string;
  sellerTaxNo: string;
  amountCents: number;
  taxCents: number;
  invoiceStatus: InvoiceStatus;
  validity: InvoiceValidity;
  riskLevel: RiskLevel;
  riskItems: Invoice['riskItems'];
  contractNo: string | null;
  /** 工程归属（可选）。为 null 表示未关联工程 */
  projectName?: string | null;
  /** 票面备注（可选）。建筑服务发票的备注里通常写着工程名称与工程地址 */
  remarks?: string | null;
  handlerName: string;
  rejectCount?: number;
  lastRejectReason?: string | null;
  reportNo?: string | null;
}

/**
 * mock 专用：把「票面备注」随 Invoice 对象带着走。
 *
 * 【为什么不加进 Invoice 类型】真实接口里 `remarks` 是**列表项**上的字段，
 * 来自 `invoice_ocr`（发票主表没有 remark 列）。为了让演示数据能带着它流动
 * 而往主类型上加一个实际不存在的字段，会让类型撒谎。
 */
export type MockInvoice = Invoice & { remarks?: string | null };

export function makeInvoice(seed: InvoiceSeed): MockInvoice {
  const totalCents = seed.amountCents + seed.taxCents;
  const status = seed.invoiceStatus;
  const pushed = status === InvoiceStatus.PUSHED || status === InvoiceStatus.BOOKED || status === InvoiceStatus.ARCHIVED;
  const checked = status !== InvoiceStatus.DRAFT && status !== InvoiceStatus.CHECKING;

  const invoice: MockInvoice = {
    ...base(seed.id, seed.daysAgo),
    bizNo: seed.bizNo,
    // 演示数据都是发票业务上传的；报销来源的票由费用模块的页面单独构造
    bizSource: InvoiceSource.INVOICE,
    invoiceType: seed.invoiceType,
    invoiceCode: seed.invoiceCode,
    invoiceNo: seed.invoiceNo,
    invoiceDate: taxDate(seed.daysAgo + 2),
    sellerName: seed.sellerName,
    sellerTaxNo: seed.sellerTaxNo,
    buyerName: '演示科技有限公司',
    buyerTaxNo: '91370100MA3D9K8X2P',
    amountCents: seed.amountCents,
    taxCents: seed.taxCents,
    totalCents,
    totalInWords: '',
    invoiceStatus: seed.invoiceStatus,
    validity: seed.validity,
    riskLevel: seed.riskLevel,
    riskItems: seed.riskItems,
    contractId: seed.contractNo ? `ct-${seed.contractNo}` : null,
    contractNo: seed.contractNo,
    projectId: seed.projectName ? `pj-${seed.projectName}` : null,
    projectName: seed.projectName ?? null,
    fileId: `file-${seed.id}`,
    fileName: `${seed.invoiceNo}.pdf`,
    fileSize: 286_720,
    fileMime: 'application/pdf',
    fileHash: `hash-${seed.id}`,
    handlerId: 'u-handler',
    handlerName: seed.handlerName,
    // 票面备注：导出专用报表要用。挂在 mock 的 Invoice 上随对象流到列表项
    remarks: seed.remarks ?? null,
    deptId: 'd-project',
    reportNo: seed.reportNo ?? (checked ? `RPT2026${seed.bizNo.slice(-6)}` : null),
    rejectCount: seed.rejectCount ?? 0,
    lastRejectReason: seed.lastRejectReason ?? null,
    submittedAt: checked ? iso(seed.daysAgo, 9, 30) : null,
    checkedAt: checked ? iso(seed.daysAgo, 9, 33) : null,
    approvedAt:
      status === InvoiceStatus.APPROVED ||
      status === InvoiceStatus.PUSHED ||
      status === InvoiceStatus.BOOKED ||
      status === InvoiceStatus.ARCHIVED
        ? iso(Math.max(0, seed.daysAgo - 1), 11, 5)
        : null,
    archivedAt: status === InvoiceStatus.ARCHIVED ? iso(1, 18, 0) : null,
    tplus: pushed ? makeTplusSync(seed.id, status === InvoiceStatus.BOOKED || status === InvoiceStatus.ARCHIVED) : null,
  };

  return invoice;
}

export function makeTplusSync(invoiceId: string, booked: boolean): TplusSync {
  return {
    ...base(`tp-${invoiceId}`, 2),
    invoiceId,
    status: booked ? TplusSyncStatus.BOOKED : TplusSyncStatus.PUSHED,
    accountSet: '演示科技-默认账套',
    tplusBillNo: `T+${invoiceId.slice(-4).toUpperCase()}${booked ? '8' : '7'}12`,
    pushPayload: {
      invoiceType: '01',
      invoiceNo: '24112000000098761234',
      totalAmount: '125800.00',
      note: '仅同步业务数据，不生成凭证',
    },
    pushResponse: { code: '0', message: '成功' },
    pushedAt: iso(2, 14, 20),
    pushError: null,
    retryCount: 0,
    // 凭证字段仅由 T+ 回写产生，系统不产生
    voucherNo: booked ? '记-2026-08-0031' : null,
    bookedAt: booked ? iso(1, 9, 40) : null,
    bookingStatus: booked ? '已记账' : null,
    callbackPayload: booked ? { voucherNo: '记-2026-08-0031', bookingStatus: '已记账' } : null,
    callbackAt: booked ? iso(1, 9, 40) : null,
    // 凭证回写轮询状态。演示数据里已记账的票视作轮询确认过，
    // 未记账的票轮询过若干次仍无凭证 —— 正是"事实 3"的真实形态
    voucherPollCount: booked ? 1 : 6,
    voucherFirstPollAt: iso(1, 9, 40),
    voucherLastPollAt: iso(0, 8, 0),
    voucherPollSeen: null,
    unconfirmedAt: null,
    unconfirmedReason: null,
  };
}

export function makeInvoiceListItem(invoice: Invoice, overrides: Partial<InvoiceListItem> = {}): InvoiceListItem {
  const nodeByStatus: Partial<Record<InvoiceStatus, [string, string]>> = {
    [InvoiceStatus.DRAFT]: ['待提交校验', '项目部经办人'],
    [InvoiceStatus.CHECKING]: ['自动校验中', '系统'],
    [InvoiceStatus.PENDING_REVIEW]: ['财务初审', '财务初审岗'],
    [InvoiceStatus.REJECTED]: ['经办人处理退回', '项目部经办人'],
    [InvoiceStatus.APPROVED]: ['推送 T+ 待办', '财务初审岗'],
    [InvoiceStatus.PUSHED]: ['T+ 端选择科目记账', '财务记账岗'],
    [InvoiceStatus.BOOKED]: ['等待归档', '财务记账岗'],
    [InvoiceStatus.ARCHIVED]: ['已归档', '-'],
  };
  const node = nodeByStatus[invoice.invoiceStatus] ?? [null, null];
  return {
    ...invoice,
    currentNodeName: node[0] ?? null,
    pendingRoleName: node[1] ?? null,
    handlerName: invoice.handlerName ?? null,
    remarks: (invoice as { remarks?: string | null }).remarks ?? null,
    tplusVoucherNo: invoice.tplus?.voucherNo ?? null,
    /* 与线上同一套规则：演示数据没有明细行，因此走"由税额÷不含税金额推算"
     * 那一支，并如实带上「（推算）」标注——离线演示也不该把推算值冒充成票面税率 */
    taxRateText: resolveInvoiceTaxRateText({
      detailRates: [],
      amountCents: invoice.amountCents,
      taxCents: invoice.taxCents,
    }),
    ...overrides,
  };
}

export function makeOcr(invoice: Invoice, opts: { lowConfidence?: boolean; manuallyEdited?: boolean } = {}): InvoiceOcr {
  const amountYuan = ((invoice.totalCents ?? 0) / 100).toFixed(2);
  const taxYuan = ((invoice.taxCents ?? 0) / 100).toFixed(2);
  const netYuan = ((invoice.amountCents ?? 0) / 100).toFixed(2);
  const low = opts.lowConfidence === true;

  return {
    ...base(`ocr-${invoice.id}`, 3),
    invoiceId: invoice.id,
    engine: 'aliyun',
    fields: {
      invoiceCode: invoice.invoiceCode,
      invoiceNumber: invoice.invoiceNo,
      invoiceDate: invoice.invoiceDate,
      machineCode: '66123456789',
      checkCode: '04821 33915 77120 66321',
      passwordArea: '04821 33915 77120 66321',
      invoiceType: '增值税专用发票',
      purchaserName: invoice.buyerName,
      purchaserTaxNumber: invoice.buyerTaxNo,
      purchaserContactInfo: '山东省济南市历下区经十路 1000 号 0531-88886666',
      purchaserBankAccountInfo: '中国银行济南分行 6217********1234',
      sellerName: invoice.sellerName,
      sellerTaxNumber: invoice.sellerTaxNo,
      sellerContactInfo: '山东省济南市高新区舜华路 200 号 0531-88997777',
      sellerBankAccountInfo: '招商银行济南高新支行 6214********8899',
      invoiceAmountPreTax: netYuan,
      invoiceTax: taxYuan,
      totalAmount: amountYuan,
      totalAmountInWords: '壹拾贰万伍仟捌佰圆整',
      recipient: '李四',
      reviewer: '王五',
      drawer: '赵六',
      remarks: '合同款（进度款 30%）',
      title: null,
      details: [
        {
          itemName: '建筑材料*钢材',
          specification: 'HRB400 12mm',
          unit: '吨',
          quantity: '25.5',
          unitPrice: '3,800.00',
          amount: netYuan,
          taxRate: '13%',
          tax: taxYuan,
        },
      ],
    },
    confidence: {
      invoiceCode: 99,
      invoiceNumber: 99,
      invoiceDate: 98,
      sellerName: low ? 62 : 97,
      sellerTaxNumber: low ? 58 : 96,
      totalAmount: 97,
      invoiceAmountPreTax: 96,
      invoiceTax: 95,
    },
    overallConfidence: low ? 78 : 96,
    rawPayload: { RequestId: 'DEMO-OCR-0001', Data: { note: '演示数据，非真实报文' } },
    durationMs: 1260,
    manuallyEdited: opts.manuallyEdited ?? false,
    editedBy: opts.manuallyEdited ? 'u-handler' : null,
    editedAt: opts.manuallyEdited ? iso(3, 11, 0) : null,
    editHistory: opts.manuallyEdited
      ? [
          {
            field: 'sellerTaxNumber',
            before: '9137O100MA3D9K8X2P',
            after: '91370100MA3D9K8X2P',
            operatorId: 'u-handler',
            operatorName: '孙经办',
            at: iso(3, 11, 0),
            reason: '票面字符 O 与数字 0 识别错误，按原件修正',
          },
        ]
      : [],
  };
}

export function makeSupplierCheck(
  invoice: Invoice,
  opts: { abnormal?: boolean; scopeMatched?: boolean; confidence?: number; status?: CheckStatus } = {},
): SupplierCheck {
  const status = opts.status ?? CheckStatus.SUCCESS;
  return {
    ...base(`sc-${invoice.id}`, 3),
    invoiceId: invoice.id,
    invoiceCode: invoice.invoiceCode,
    invoiceNo: invoice.invoiceNo,
    keyword: invoice.sellerName ?? '',
    keyNo: 'DEMOKEY0001',
    companyName: invoice.sellerName,
    creditCode: invoice.sellerTaxNo,
    status: opts.abnormal ? '注销' : '存续',
    scope: '建筑材料、钢材、水泥、五金交电的批发与零售；普通货物道路运输。',
    registCapi: '36225万元',
    operName: '王法定代表人',
    establishDate: '2012-06-18',
    address: '山东省济南市高新区舜华路 200 号',
    scopeMatchConfidence: opts.confidence ?? 92,
    scopeMatched: opts.scopeMatched ?? true,
    matchedItems: ['建筑材料*钢材'],
    checkStatus: status,
    errorMessage: status === CheckStatus.UNAVAILABLE ? '企查查接口连接超时（ETIMEDOUT）' : null,
    rawPayload: { Status: '200', Result: { Name: invoice.sellerName, Status: '存续' } },
    calledAt: status === CheckStatus.UNAVAILABLE ? null : iso(3, 9, 31),
    durationMs: status === CheckStatus.UNAVAILABLE ? null : 820,
  };
}

export function makeTaxCheck(
  invoice: Invoice,
  opts: { validity?: InvoiceValidity; status?: CheckStatus; locked?: boolean; mismatch?: string[] } = {},
): TaxCheck {
  const validity = opts.validity ?? invoice.validity;
  const status = opts.status ?? CheckStatus.SUCCESS;
  return {
    ...base(`tc-${invoice.id}`, 3),
    invoiceId: invoice.id,
    invoiceCode: invoice.invoiceCode,
    invoiceNo: invoice.invoiceNo,
    invoiceDate: invoice.invoiceDate,
    channel: status === CheckStatus.UNAVAILABLE ? 'aliyun' : 'aliyun',
    validity,
    channelCode: validity === InvoiceValidity.VALID ? '0001' : '0002',
    channelMessage:
      validity === InvoiceValidity.RED_FLUSHED
        ? '该发票已冲红'
        : validity === InvoiceValidity.VOIDED
          ? '该发票已作废'
          : '查验成功',
    verified: validity === InvoiceValidity.VALID,
    invalidMark: validity === InvoiceValidity.RED_FLUSHED ? 'Y' : validity === InvoiceValidity.VOIDED ? 'Y' : 'N',
    blueInvoiceCode: validity === InvoiceValidity.RED_FLUSHED ? '3700191130' : null,
    blueInvoiceNo: validity === InvoiceValidity.RED_FLUSHED ? '11223344' : null,
    taxFields: { InvoiceNo: invoice.invoiceNo, TotalAmount: (invoice.totalCents ?? 0) / 100 },
    mismatchedFields: opts.mismatch ?? null,
    inputLockStatus: opts.locked ? 'LOCKED' : 'NOT_ATTEMPTED',
    inputLockedAt: opts.locked ? iso(3, 9, 32) : null,
    inputLockedBy: opts.locked ? '财务初审岗' : null,
    checkStatus: status,
    errorMessage: status === CheckStatus.UNAVAILABLE ? '税务总局查验接口 503，已标记为暂不可用' : null,
    rawPayload: { code: '0001', data: { checkResult: validity } },
    calledAt: status === CheckStatus.UNAVAILABLE ? null : iso(3, 9, 32),
    durationMs: status === CheckStatus.UNAVAILABLE ? null : 1450,
    fromCache: false,
  };
}

export function makeContractRef(invoice: Invoice, opts: { status?: CheckStatus; amountExceeded?: boolean } = {}): ContractRef {
  const status = opts.status ?? CheckStatus.SUCCESS;
  return {
    ...base(`cr-${invoice.id}`, 3),
    invoiceId: invoice.id,
    contractId: invoice.contractId,
    contractNo: invoice.contractNo ?? 'HT-2026-0001',
    amountExceeded: opts.amountExceeded ?? false,
    categoryMatched: true,
    stageMatched: true,
    mismatchDetails: opts.amountExceeded ? ['本次开票后累计开票金额将超出合同金额 12,000.00 元'] : null,
    checkStatus: status,
  };
}

export function makeInvoiceCheck(
  invoice: Invoice,
  opts: {
    ocrStatus?: CheckStatus;
    supplierStatus?: CheckStatus;
    taxStatus?: CheckStatus;
    contractStatus?: CheckStatus;
    manualOverride?: InvoiceCheck['manualOverride'];
  } = {},
): InvoiceCheck {
  const riskItems = invoice.riskItems;
  const tplusBlocked = riskItems.some((i) => i.level === RiskLevel.HIGH);
  const ocrStatus = opts.ocrStatus ?? CheckStatus.SUCCESS;
  const supplierStatus = opts.supplierStatus ?? CheckStatus.SUCCESS;
  const taxStatus = opts.taxStatus ?? CheckStatus.SUCCESS;
  const contractStatus = opts.contractStatus ?? CheckStatus.SUCCESS;
  // 非成功状态必须能解释原因，否则报告里只会留下一个没有上下文的「不可用」
  const stepMessage = (label: string, status: CheckStatus): string | null => {
    if (status === CheckStatus.UNAVAILABLE) {
      return `${label}接口暂不可用（连接超时 / 服务返回 503），本项自动校验已跳过，可由人工核验后接管`;
    }
    if (status === CheckStatus.FAILED) return `${label}执行失败，可点击「重试校验」重新发起`;
    if (status === CheckStatus.MANUAL_OVERRIDE) return `${label}已由人工核验并接管，核验说明见发票详情的接管记录`;
    return null;
  };
  return {
    ...base(`chk-${invoice.id}`, 3),
    invoiceId: invoice.id,
    reportNo: invoice.reportNo ?? `RPT-${invoice.bizNo}`,
    ocrStatus,
    supplierStatus,
    taxStatus,
    contractStatus,
    riskLevel: invoice.riskLevel,
    riskItems,
    manualOverride: opts.manualOverride ?? null,
    tplusBlocked,
    checkedAt: iso(3, 9, 33),
    durationMs: 3860,
    orchestrationLog: [
      /* 带上 mode/impl：前端就是靠这两个字段渲染通道来源标签的。
       * 这里刻意用**真实调用**作为演示数据的默认值，让 mock 环境下
       * 演示出来的是"正常情况"；要演示未走真实通道的告警，把某个 step
       * 的 mode 改成 ProviderMode.SANDBOX 或 UNAVAILABLE 即可。 */
      {
        step: 'OCR',
        status: ocrStatus,
        durationMs: 1260,
        message: stepMessage('OCR 识别', ocrStatus),
        mode: ProviderMode.REAL,
        impl: 'aliyun-recognize-invoice',
      },
      {
        step: 'SUPPLIER_CHECK',
        status: supplierStatus,
        durationMs: 820,
        message: stepMessage('企查查主体校验', supplierStatus),
        mode: ProviderMode.REAL,
        impl: 'qcc-GetBasicDetailsByName',
      },
      {
        step: 'TAX_CHECK',
        status: taxStatus,
        durationMs: 1450,
        message: stepMessage('税务局验真', taxStatus),
        fromCache: false,
        mode: ProviderMode.REAL,
        impl: 'aliyun-verify-vat-invoice',
      },
      {
        step: 'CONTRACT_MATCH',
        status: contractStatus,
        durationMs: 180,
        message: stepMessage('合同比对', contractStatus),
      },
      { step: 'RISK_GRADING', status: CheckStatus.SUCCESS, durationMs: 12, message: null },
    ],
  };
}

/**
 * 「外部校验不可用」演示现场（待后端接口就绪后移除）
 *
 * 真实后端不会主动制造接口故障，所以这里固定两张演示发票来覆盖「人工核验并接管」这条通道：
 *   - 11111111-1111-4111-8111-111111111101：企查查超时 + 验真失败，尚无接管记录（演示接管入口与核验说明表单）；
 *   - 11111111-1111-4111-8111-111111111102：同上，但已有一条人工接管记录（演示报告页的接管说明与留痕字段）。
 * 直接打开 /business/invoices/<id> 即可查看：
 * 后端没有这两张票，且 id 是合法 UUID，会返回 404 → 前端按降级策略使用本地数据。
 */
export const UNAVAILABLE_CHECK_DEMO: Record<
  string,
  { ocrStatus?: CheckStatus; supplierStatus?: CheckStatus; taxStatus?: CheckStatus; manualOverride?: InvoiceCheck['manualOverride'] }
> = {
  // inv-004 的风险项里写了「企查查外部校验暂不可用」，这里让校验状态保持一致，
  // 否则会出现「风险项说不可用、校验项却显示成功」的自相矛盾演示数据
  '00000000-0000-4000-8000-000000000004': {
    supplierStatus: CheckStatus.UNAVAILABLE,
  },
  '11111111-1111-4111-8111-111111111101': {
    supplierStatus: CheckStatus.UNAVAILABLE,
    taxStatus: CheckStatus.FAILED,
  },
  '11111111-1111-4111-8111-111111111102': {
    supplierStatus: CheckStatus.UNAVAILABLE,
    taxStatus: CheckStatus.MANUAL_OVERRIDE,
    manualOverride: {
      operatorId: 'u-finance',
      operatorName: '李初审',
      reason:
        '企查查接口连续超时、税务局验真返回 503，已改为人工核验：通过国家企业信用信息公示系统核对供应商主体存续，并在电子税务局手工查验发票状态为「正常」，票面信息与合同一致，同意继续流转。',
      items: ['供应商主体资格', '发票真伪与状态'],
      at: iso(1, 15, 20),
    },
  },
};

export function makeFlowInstance(invoice: Invoice): FlowInstance {
  const returned = invoice.invoiceStatus === InvoiceStatus.REJECTED;
  const finished =
    invoice.invoiceStatus === InvoiceStatus.BOOKED || invoice.invoiceStatus === InvoiceStatus.ARCHIVED;
  return {
    ...base(`fi-${invoice.id}`, invoice.rejectCount > 0 ? 6 : 3),
    templateId: 'ft-default',
    bizType: 'INVOICE',
    bizId: invoice.id,
    status: finished
      ? FlowInstanceStatus.COMPLETED
      : returned
        ? FlowInstanceStatus.RETURNED
        : FlowInstanceStatus.RUNNING,
    currentNodeKey: returned ? 'handler_fix' : 'finance_review',
    currentNodeName: returned ? '经办人处理退回' : '财务初审',
    variables: {
      amount: invoice.totalCents,
      riskLevel: invoice.riskLevel,
      invoiceType: invoice.invoiceType,
    },
    startedAt: iso(3, 9, 30),
    finishedAt: finished ? iso(1, 18, 0) : null,
    tasks: makeFlowTasks(invoice),
  };
}

export function makeFlowTasks(invoice: Invoice): FlowTask[] {
  const tasks: FlowTask[] = [
    {
      ...base(`ft1-${invoice.id}`, 3),
      instanceId: `fi-${invoice.id}`,
      // 待办必须带业务类型与对象 ID——前端据它决定跳到哪个详情页。
      // 从前是拿 instanceId 当发票 ID 用（`fi-xxx`），真实数据下必然 404。
      bizType: BizType.INVOICE,
      bizId: invoice.id,
      bizSummary: {
        title: invoice.bizNo,
        subtitle: invoice.sellerName,
        amountCents: invoice.totalCents,
        statusLabel: invoice.invoiceStatus,
        riskLevel: invoice.riskLevel,
      },
      nodeKey: 'auto_check',
      nodeName: '自动校验',
      status: FlowTaskStatus.APPROVED,
      assigneeRoleCode: null,
      assigneeUserId: null,
      assigneeUserName: '系统',
      action: null,
      comment: 'OCR / 企查查 / 税务局 / 合同比对 执行完成',
      isSpecialRelease: false,
      specialReleaseReason: null,
      nextNodeKey: 'finance_review',
      dueAt: null,
      handledAt: iso(3, 9, 33),
    },
    {
      ...base(`ft2-${invoice.id}`, 3),
      instanceId: `fi-${invoice.id}`,
      bizType: BizType.INVOICE,
      bizId: invoice.id,
      nodeKey: 'finance_review',
      nodeName: '财务初审',
      status:
        invoice.invoiceStatus === InvoiceStatus.PENDING_REVIEW
          ? FlowTaskStatus.PENDING
          : invoice.invoiceStatus === InvoiceStatus.REJECTED
            ? FlowTaskStatus.REJECTED
            : FlowTaskStatus.APPROVED,
      assigneeRoleCode: DefaultRoleCode.FINANCE_REVIEWER,
      assigneeUserId: invoice.invoiceStatus === InvoiceStatus.PENDING_REVIEW ? null : 'u-reviewer',
      assigneeUserName: invoice.invoiceStatus === InvoiceStatus.PENDING_REVIEW ? null : '周初审',
      action: null,
      comment: invoice.lastRejectReason,
      isSpecialRelease: false,
      specialReleaseReason: null,
      nextNodeKey: invoice.invoiceStatus === InvoiceStatus.REJECTED ? 'handler_fix' : 'push_tplus',
      dueAt: iso(-1, 9, 33),
      handledAt: invoice.invoiceStatus === InvoiceStatus.PENDING_REVIEW ? null : iso(2, 11, 5),
    },
  ];
  return tasks;
}

export function makeInvoiceDetail(invoice: Invoice): InvoiceDetail {
  return {
    ...invoice,
    ocr: makeOcr(invoice, { lowConfidence: invoice.riskItems.some((i) => i.code === RiskItemCode.OCR_LOW_CONFIDENCE) }),
    check: makeInvoiceCheck(invoice, UNAVAILABLE_CHECK_DEMO[invoice.id] ?? {}),
    supplierCheck: makeSupplierCheck(invoice, {
      abnormal: invoice.riskItems.some((i) => i.code === RiskItemCode.SUPPLIER_ABNORMAL),
      scopeMatched: !invoice.riskItems.some((i) => i.code === RiskItemCode.SCOPE_MISMATCH),
      confidence: invoice.riskItems.some((i) => i.code === RiskItemCode.SCOPE_MISMATCH) ? 46 : 92,
    }),
    taxCheck: makeTaxCheck(invoice),
    contractRef: makeContractRef(invoice),
    tplus: invoice.tplus ?? null,
    flowInstance: makeFlowInstance(invoice),
  };
}

export function makeCheckReport(invoice: Invoice): CheckReport {
  const detail = makeInvoiceDetail(invoice);
  return {
    reportNo: invoice.reportNo ?? `RPT-${invoice.bizNo}`,
    invoice,
    ocr: detail.ocr
      ? {
          engine: detail.ocr.engine,
          fields: detail.ocr.fields as unknown as Record<string, string | null>,
          confidence: detail.ocr.confidence,
          overallConfidence: detail.ocr.overallConfidence,
          manuallyEdited: detail.ocr.manuallyEdited,
          editHistory: detail.ocr.editHistory,
        }
      : null,
    supplier: detail.supplierCheck,
    tax: detail.taxCheck,
    contract: detail.contractRef
      ? {
          contractNo: detail.contractRef.contractNo,
          amountExceeded: detail.contractRef.amountExceeded,
          categoryMatched: detail.contractRef.categoryMatched,
          stageMatched: detail.contractRef.stageMatched,
          mismatchDetails: detail.contractRef.mismatchDetails,
        }
      : null,
    riskLevel: invoice.riskLevel,
    riskItems: invoice.riskItems,
    checkUnavailable:
      detail.check?.taxStatus === CheckStatus.UNAVAILABLE ||
      detail.check?.supplierStatus === CheckStatus.UNAVAILABLE ||
      detail.check?.ocrStatus === CheckStatus.UNAVAILABLE,
    tplusBlocked: detail.check?.tplusBlocked ?? false,
    orchestrationLog: detail.check?.orchestrationLog ?? [],
    generatedAt: iso(3, 9, 34),
  };
}

/* ==========================================================================
 * 组织、角色、流程
 * ========================================================================== */

export const mockDepts: Dept[] = [
  {
    ...base('d-project', 90),
    name: '项目部',
    parentId: null,
    path: '/d-project',
    sortOrder: 1,
  },
  { ...base('d-project-1', 90), name: '华东项目组', parentId: 'd-project', path: '/d-project/d-project-1', sortOrder: 1 },
  { ...base('d-project-2', 90), name: '华北项目组', parentId: 'd-project', path: '/d-project/d-project-2', sortOrder: 2 },
  { ...base('d-finance', 90), name: '财务部', parentId: null, path: '/d-finance', sortOrder: 2 },
  { ...base('d-legal', 90), name: '法务部', parentId: null, path: '/d-legal', sortOrder: 3 },
  { ...base('d-tech', 90), name: '技术实施组', parentId: null, path: '/d-tech', sortOrder: 4 },
];

export const mockRoles: Role[] = [
  {
    ...base('r-admin', 90),
    code: DefaultRoleCode.TENANT_ADMIN,
    name: '租户管理员',
    description: '租户内全部配置与用户管理',
    isSystem: true,
    dataScope: DataScope.TENANT,
    customDeptIds: null,
    amountLimitCents: null,
    maxRiskLevel: null,
    userCount: 1,
  },
  {
    ...base('r-handler', 90),
    code: DefaultRoleCode.PROJECT_HANDLER,
    name: '项目部经办人',
    description: '上传发票、关联合同、处理退回',
    isSystem: true,
    dataScope: DataScope.DEPT,
    customDeptIds: null,
    amountLimitCents: null,
    maxRiskLevel: null,
    userCount: 3,
  },
  {
    ...base('r-reviewer', 90),
    code: DefaultRoleCode.FINANCE_REVIEWER,
    name: '财务初审岗',
    description: '审阅校验报告、人工复核、确认推送',
    isSystem: true,
    dataScope: DataScope.TENANT,
    customDeptIds: null,
    amountLimitCents: 50_000_000,
    maxRiskLevel: RiskLevel.MEDIUM,
    userCount: 1,
  },
  {
    ...base('r-bookkeeper', 90),
    code: DefaultRoleCode.FINANCE_BOOKKEEPER,
    name: '财务记账岗',
    description: 'T+ 端选择科目、生成凭证、回写',
    isSystem: true,
    dataScope: DataScope.TENANT,
    customDeptIds: null,
    amountLimitCents: null,
    maxRiskLevel: null,
    userCount: 1,
  },
  {
    ...base('r-legal', 90),
    code: DefaultRoleCode.LEGAL,
    name: '法务岗',
    description: '合同风险、归档、事后抽查',
    isSystem: true,
    dataScope: DataScope.TENANT,
    customDeptIds: null,
    amountLimitCents: null,
    maxRiskLevel: null,
    userCount: 1,
  },
  {
    ...base('r-tech', 90),
    code: DefaultRoleCode.TECH_OPS,
    name: '技术实施组',
    description: '接口配置、日志查看、异常处理',
    isSystem: true,
    dataScope: DataScope.TENANT,
    customDeptIds: null,
    amountLimitCents: null,
    maxRiskLevel: null,
    userCount: 1,
  },
];

function role(id: string, code: string, name: string) {
  return { id, code, name };
}

export const mockUsers: User[] = [
  {
    ...base('u-admin', 90),
    username: 'admin',
    name: '张管理',
    deptId: 'd-finance',
    deptName: '财务部',
    phone: '13800000001',
    email: 'admin@demo.example.com',
    status: UserStatus.ACTIVE,
    dingtalkUserId: 'ding_admin',
    lastLoginAt: iso(0, 8, 55),
    roles: [role('r-admin', DefaultRoleCode.TENANT_ADMIN, '租户管理员')],
  },
  {
    ...base('u-handler', 90),
    username: 'jingban',
    name: '孙经办',
    deptId: 'd-project-1',
    deptName: '华东项目组',
    phone: '13800000002',
    email: 'jingban@demo.example.com',
    status: UserStatus.ACTIVE,
    dingtalkUserId: 'ding_jingban',
    lastLoginAt: iso(0, 9, 10),
    roles: [role('r-handler', DefaultRoleCode.PROJECT_HANDLER, '项目部经办人')],
  },
  {
    ...base('u-reviewer', 90),
    username: 'chushen',
    name: '周初审',
    deptId: 'd-finance',
    deptName: '财务部',
    phone: '13800000003',
    email: 'chushen@demo.example.com',
    status: UserStatus.ACTIVE,
    dingtalkUserId: 'ding_chushen',
    lastLoginAt: iso(0, 9, 20),
    roles: [role('r-reviewer', DefaultRoleCode.FINANCE_REVIEWER, '财务初审岗')],
  },
  {
    ...base('u-bookkeeper', 90),
    username: 'jizhang',
    name: '吴记账',
    deptId: 'd-finance',
    deptName: '财务部',
    phone: '13800000004',
    email: 'jizhang@demo.example.com',
    status: UserStatus.ACTIVE,
    dingtalkUserId: 'ding_jizhang',
    lastLoginAt: iso(1, 15, 30),
    roles: [role('r-bookkeeper', DefaultRoleCode.FINANCE_BOOKKEEPER, '财务记账岗')],
  },
  {
    ...base('u-legal', 90),
    username: 'fawu',
    name: '郑法务',
    deptId: 'd-legal',
    deptName: '法务部',
    phone: '13800000005',
    email: 'fawu@demo.example.com',
    status: UserStatus.ACTIVE,
    dingtalkUserId: 'ding_fawu',
    lastLoginAt: iso(2, 14, 10),
    roles: [role('r-legal', DefaultRoleCode.LEGAL, '法务岗')],
  },
  {
    ...base('u-tech', 90),
    username: 'jishu',
    name: '陈技术',
    deptId: 'd-tech',
    deptName: '技术实施组',
    phone: '13800000006',
    email: 'jishu@demo.example.com',
    status: UserStatus.ACTIVE,
    dingtalkUserId: 'ding_jishu',
    lastLoginAt: iso(0, 9, 5),
    roles: [role('r-tech', DefaultRoleCode.TECH_OPS, '技术实施组')],
  },
];

export const mockFlowTemplates: FlowTemplate[] = [
  {
    ...base('ft-default', 90),
    code: 'INVOICE_DEFAULT',
    name: '发票核验默认流程',
    description: '经办人上传 → 自动校验 → 财务初审 →（分支）推送 T+ 待办 → 记账回写 → 归档',
    bizType: BizType.INVOICE,
    isDefault: true,
    version: 3,
    isActive: true,
    nodes: [
      {
        id: 'n1',
        templateId: 'ft-default',
        key: 'start',
        name: '经办人上传发票',
        type: FlowNodeType.START,
        sortOrder: 1,
        assigneeRoleCode: DefaultRoleCode.PROJECT_HANDLER,
        requiredPermission: null,
        condition: null,
        nextKey: 'auto_check',
        defaultNextKey: null,
        timeoutHours: null,
        allowSpecialRelease: false,
        description: '上传发票原件并关联合同，支持批量导入',
      },
      {
        id: 'n2',
        templateId: 'ft-default',
        key: 'auto_check',
        name: '系统自动校验',
        type: FlowNodeType.AUTO,
        sortOrder: 2,
        assigneeRoleCode: null,
        requiredPermission: null,
        condition: null,
        nextKey: null,
        defaultNextKey: 'finance_review',
        timeoutHours: null,
        allowSpecialRelease: false,
        description: 'OCR 识别 + 企查查主体校验 + 税务局验真 + 合同比对；仅做风险标记，不跳过人工确认',
      },
      {
        id: 'n3',
        templateId: 'ft-default',
        key: 'risk_branch',
        name: '风险等级分支',
        type: FlowNodeType.CONDITION,
        sortOrder: 3,
        assigneeRoleCode: null,
        requiredPermission: null,
        condition: {
          logic: 'OR',
          rules: [
            { field: 'riskLevel', operator: 'EQ', value: RiskLevel.HIGH },
            { field: 'amount', operator: 'GT', value: 50_000_000 },
          ],
        },
        nextKey: 'special_release',
        defaultNextKey: 'finance_review',
        timeoutHours: null,
        allowSpecialRelease: false,
        description: '高风险（红冲/作废/供应商异常）或金额超 50 万时，走特例放行专项审批',
      },
      {
        id: 'n4',
        templateId: 'ft-default',
        key: 'special_release',
        name: '特例放行审批',
        type: FlowNodeType.APPROVAL,
        sortOrder: 4,
        assigneeRoleCode: DefaultRoleCode.FINANCE_REVIEWER,
        requiredPermission: 'invoice:special-release',
        condition: null,
        nextKey: 'push_tplus',
        defaultNextKey: null,
        timeoutHours: 24,
        allowSpecialRelease: true,
        description: '必须填写专项放行依据，全程留痕；默认阻断流转 T+，此处是唯一放行通道',
      },
      {
        id: 'n5',
        templateId: 'ft-default',
        key: 'finance_review',
        name: '财务初审',
        type: FlowNodeType.APPROVAL,
        sortOrder: 5,
        assigneeRoleCode: DefaultRoleCode.FINANCE_REVIEWER,
        requiredPermission: 'invoice:review',
        condition: null,
        nextKey: 'push_tplus',
        defaultNextKey: null,
        timeoutHours: 24,
        allowSpecialRelease: true,
        description: '人工审阅校验报告后通过或退回；自动化不替代此节点',
      },
      {
        id: 'n6',
        templateId: 'ft-default',
        key: 'push_tplus',
        name: '推送 T+ 待办业务池',
        type: FlowNodeType.PUSH,
        sortOrder: 6,
        assigneeRoleCode: DefaultRoleCode.FINANCE_REVIEWER,
        requiredPermission: 'tplus:push',
        condition: null,
        nextKey: 'tplus_booking',
        defaultNextKey: null,
        timeoutHours: null,
        allowSpecialRelease: false,
        description: '仅同步业务数据，不生成凭证',
      },
      {
        id: 'n7',
        templateId: 'ft-default',
        key: 'tplus_booking',
        name: 'T+ 端人工选择科目生成凭证',
        type: FlowNodeType.APPROVAL,
        sortOrder: 7,
        assigneeRoleCode: DefaultRoleCode.FINANCE_BOOKKEEPER,
        requiredPermission: 'tplus:read',
        condition: null,
        nextKey: 'archive',
        defaultNextKey: null,
        timeoutHours: 72,
        allowSpecialRelease: false,
        description: '凭证由财务人员在 T+ 端生成，本平台不生成凭证，仅接收凭证号回写',
      },
      {
        id: 'n8',
        templateId: 'ft-default',
        key: 'archive',
        name: '归档',
        type: FlowNodeType.END,
        sortOrder: 8,
        assigneeRoleCode: null,
        requiredPermission: null,
        condition: null,
        nextKey: null,
        defaultNextKey: null,
        timeoutHours: null,
        allowSpecialRelease: false,
        description: '生成档案快照，支持按合同、供应商、时间检索追溯',
      },
    ],
  },
];

/* ==========================================================================
 * 合同、档案、日志、配置
 * ========================================================================== */

/**
 * 合同台账演示数据。
 *
 * 【为什么每一条都把新增字段写全】`Contract` 现在包含类型/层级/工期/税率、
 * 以及**读时聚合**的执行进度（剩余可开票、已付款、剩余应付）。
 * 缺字段会让 mock 与接口返回的形状分叉，而 `usePagedList` 的降级路径
 * 恰恰是拿 mock 渲染页面的——分叉了只有真机才暴露。
 * 层级按类型推导（与后端 `CONTRACT_LEVEL_OF` 同一规则），不手写死值。
 */
export const mockContracts: Contract[] = [
  {
    ...base('ct-HT-2026-0001', 60),
    contractNo: 'HT-2026-0001',
    name: '济南总部办公楼钢材采购合同',
    contractType: ContractType.PURCHASE,
    level: CONTRACT_LEVEL_OF[ContractType.PURCHASE],
    parentContractId: null,
    counterpartyRole: CounterpartyRole.CONTRACTOR,
    supplierName: '济南鲁建建材有限公司',
    supplierTaxNo: '91370100MA3D9K8X2P',
    amountCents: 120_000_000,
    amountExclTaxCents: 106_194_690,
    taxCents: 13_805_310,
    invoicedCents: 68_000_000,
    remainingCents: 52_000_000,
    paidCents: 60_000_000,
    payableCents: 60_000_000,
    settlementMethod: SettlementMethod.BY_PROGRESS,
    signDate: iso(60, 10, 0),
    startDate: iso(60, 0, 0).slice(0, 10),
    endDate: null,
    warrantyEndDate: null,
    settlementStage: '进度款 30%',
    categories: ['建筑材料', '钢材'],
    status: ContractStatus.ACTIVE,
    remark: '按进度分三次开票',
    handlerId: null,
    handlerName: null,
    deptId: null,
    deptName: null,
    openAlertCount: 0,
    approvedAt: iso(59, 10, 0),
    archivedAt: null,
  },
  {
    ...base('ct-HT-2026-0002', 45),
    contractNo: 'HT-2026-0002',
    name: '厂区货物运输年度框架协议',
    contractType: ContractType.SERVICE,
    level: CONTRACT_LEVEL_OF[ContractType.SERVICE],
    parentContractId: null,
    counterpartyRole: CounterpartyRole.CONTRACTOR,
    supplierName: '山东顺达物流有限公司',
    supplierTaxNo: '91370100MA3E1F2G3H',
    amountCents: 36_000_000,
    amountExclTaxCents: 33_027_523,
    taxCents: 2_972_477,
    invoicedCents: 12_000_000,
    remainingCents: 24_000_000,
    paidCents: 12_000_000,
    payableCents: 24_000_000,
    settlementMethod: SettlementMethod.BY_MONTH,
    signDate: iso(45, 9, 30),
    startDate: iso(45, 0, 0).slice(0, 10),
    endDate: null,
    warrantyEndDate: null,
    settlementStage: '月度结算',
    categories: ['运输服务'],
    status: ContractStatus.APPROVING,
    remark: null,
    handlerId: null,
    handlerName: null,
    deptId: null,
    deptName: null,
    openAlertCount: 1,
    approvedAt: null,
    archivedAt: null,
  },
  {
    ...base('ct-HT-2026-0003', 30),
    contractNo: 'HT-2026-0003',
    name: '办公设备采购合同',
    contractType: ContractType.PURCHASE,
    level: CONTRACT_LEVEL_OF[ContractType.PURCHASE],
    parentContractId: null,
    counterpartyRole: CounterpartyRole.CONTRACTOR,
    supplierName: '青岛恒信电子有限公司',
    supplierTaxNo: '91370200MA3J4K5L6M',
    amountCents: 8_600_000,
    amountExclTaxCents: 7_610_619,
    taxCents: 989_381,
    invoicedCents: 8_600_000,
    remainingCents: 0,
    paidCents: 8_600_000,
    payableCents: 0,
    settlementMethod: SettlementMethod.BY_MILESTONE,
    signDate: iso(30, 14, 0),
    startDate: iso(30, 0, 0).slice(0, 10),
    endDate: null,
    warrantyEndDate: null,
    settlementStage: '验收后一次性付款',
    categories: ['电子设备'],
    status: ContractStatus.ARCHIVED,
    remark: '合同金额已开满',
    handlerId: null,
    handlerName: null,
    deptId: null,
    deptName: null,
    openAlertCount: 0,
    approvedAt: iso(29, 10, 0),
    archivedAt: iso(10, 10, 0),
  },
];

export const mockArchives: ArchiveListItem[] = [
  {
    id: 'ar-1',
    invoiceId: mockInvoiceId('inv-008'),
    bizNo: 'BZ20260820001234',
    invoiceNo: '24112000000098761234',
    invoiceCode: '3700191130',
    invoiceType: InvoiceTypeCode.VAT_SPECIAL,
    sellerName: '济南鲁建建材有限公司',
    totalCents: 12_580_000,
    invoiceDate: taxDate(20),
    contractNo: 'HT-2026-0001',
    reportNo: 'RPT20260820001234',
    voucherNo: '记-2026-08-0031',
    riskLevel: RiskLevel.NONE,
    archivedAt: iso(18, 18, 0),
  },
  {
    id: 'ar-2',
    invoiceId: mockInvoiceId('inv-009'),
    bizNo: 'BZ20260818004321',
    invoiceNo: '24112000000098760001',
    invoiceCode: '3700191130',
    invoiceType: InvoiceTypeCode.VAT_SPECIAL,
    sellerName: '山东顺达物流有限公司',
    totalCents: 2_180_000,
    invoiceDate: taxDate(24),
    contractNo: 'HT-2026-0002',
    reportNo: 'RPT20260818004321',
    voucherNo: '记-2026-08-0022',
    riskLevel: RiskLevel.MEDIUM,
    archivedAt: iso(20, 18, 0),
  },
  {
    id: 'ar-3',
    invoiceId: 'inv-010',
    bizNo: 'BZ20260815007788',
    invoiceNo: '24312000000012345678',
    invoiceCode: null,
    invoiceType: InvoiceTypeCode.E_INVOICE_GENERAL,
    sellerName: '青岛恒信电子有限公司',
    totalCents: 986_000,
    invoiceDate: taxDate(30),
    contractNo: 'HT-2026-0003',
    reportNo: 'RPT20260815007788',
    voucherNo: null,
    riskLevel: RiskLevel.LOW,
    archivedAt: iso(28, 18, 0),
  },
];

export const mockProviderConfigs: ProviderConfig[] = [
  {
    ...base('pc-ocr', 90),
    provider: ProviderCode.OCR,
    mode: ProviderMode.REAL,
    enabled: true,
    qccBlockOnAbnormal: false,
    qccScopeThreshold: 0,
    taxEnableInputLock: false,
    taxSpecialReleaseRoleCode: null,
    tplusAccountSet: null,
    tplusFieldMapping: null,
    dingtalkAgentId: null,
    credentials: null,
    remark: '阿里云 RecognizeInvoice，按张计费',
  },
  {
    ...base('pc-tax', 90),
    provider: ProviderCode.TAX_VERIFY,
    mode: ProviderMode.SANDBOX,
    enabled: true,
    // 企查查：不做强制拦截（文档 4.1），默认关闭
    qccBlockOnAbnormal: false,
    qccScopeThreshold: 60,
    // 进项锁定是不可逆写操作，默认关闭
    taxEnableInputLock: false,
    taxSpecialReleaseRoleCode: DefaultRoleCode.FINANCE_REVIEWER,
    tplusAccountSet: null,
    tplusFieldMapping: null,
    dingtalkAgentId: null,
    credentials: null,
    remark: '未配置生产凭证时自动降级为沙箱，不产生外部调用与费用',
  },
  {
    ...base('pc-qcc', 90),
    provider: ProviderCode.QCC,
    mode: ProviderMode.REAL,
    enabled: true,
    qccBlockOnAbnormal: false,
    qccScopeThreshold: 60,
    taxEnableInputLock: false,
    taxSpecialReleaseRoleCode: null,
    tplusAccountSet: null,
    tplusFieldMapping: null,
    dingtalkAgentId: null,
    credentials: null,
    remark: '经营范围匹配阈值 60%，低于阈值仅提示不拦截',
  },
  {
    ...base('pc-tplus', 90),
    provider: ProviderCode.TPLUS,
    mode: ProviderMode.REAL,
    enabled: true,
    qccBlockOnAbnormal: false,
    qccScopeThreshold: 60,
    taxEnableInputLock: false,
    taxSpecialReleaseRoleCode: null,
    tplusAccountSet: '演示科技-默认账套',
    tplusFieldMapping: {
      invoiceNo: '发票号码',
      totalAmount: '价税合计',
      contractNo: '合同编号',
      reportNo: '校验报告编号',
    },
    dingtalkAgentId: null,
    credentials: null,
    remark: '仅推送业务数据至待办业务池，不生成凭证',
  },
  {
    ...base('pc-dingtalk', 90),
    provider: ProviderCode.DINGTALK,
    mode: ProviderMode.REAL,
    enabled: true,
    qccBlockOnAbnormal: false,
    qccScopeThreshold: 60,
    taxEnableInputLock: false,
    taxSpecialReleaseRoleCode: null,
    tplusAccountSet: null,
    tplusFieldMapping: null,
    dingtalkAgentId: 'dingtalk-agent-demo',
    credentials: null,
    remark: '待办与风险提醒推送到钉钉',
  },
];

export const mockProviderLogs: ProviderCallLog[] = [
  {
    ...base('pl-1', 0),
    provider: ProviderCode.TAX_VERIFY,
    mode: ProviderMode.SANDBOX,
    operation: 'VerifyVATInvoice',
    status: ProviderCallStatus.SUCCESS,
    resultCode: '0001',
    resultMessage: '查验成功',
    requestPayload: {
      invoiceCode: '3700191130',
      invoiceNo: '24112000000098761234',
      invoiceDate: '20260820',
      // 敏感字段在日志中已脱敏
      salerTaxNo: '9137******K8X2P',
      amount: '111327.43',
    },
    responsePayload: {
      code: '0001',
      data: { checkResult: 'VALID', invoiceType: '01', invalidMark: 'N' },
    },
    durationMs: 1450,
    retryCount: 0,
    billable: true,
    costCents: 30,
    invoiceId: mockInvoiceId('inv-008'),
    bizType: 'INVOICE',
    bizId: mockInvoiceId('inv-008'),
    errorStack: null,
    traceId: 'demo-trace-0001',
    calledAt: iso(0, 9, 31),
  },
  {
    ...base('pl-2', 0),
    provider: ProviderCode.QCC,
    mode: ProviderMode.REAL,
    operation: 'GetCompanyInfo',
    status: ProviderCallStatus.TIMEOUT,
    resultCode: null,
    resultMessage: '请求超时（3000ms）',
    requestPayload: { keyword: '山东某某贸易有限公司', creditCode: '9137******1234' },
    responsePayload: null,
    durationMs: 3000,
    retryCount: 1,
    billable: false,
    costCents: 0,
    invoiceId: mockInvoiceId('inv-004'),
    bizType: 'INVOICE',
    bizId: mockInvoiceId('inv-004'),
    errorStack: 'Error: ETIMEDOUT\n    at ProviderHttpClient.request (provider.ts:88)',
    traceId: 'demo-trace-0002',
    calledAt: iso(0, 9, 12),
  },
  {
    ...base('pl-3', 0),
    provider: ProviderCode.OCR,
    mode: ProviderMode.REAL,
    operation: 'RecognizeInvoice',
    status: ProviderCallStatus.SUCCESS,
    resultCode: '0',
    resultMessage: '识别成功',
    requestPayload: { fileName: '24112000000098761234.pdf', size: 286720 },
    responsePayload: { RequestId: 'DEMO-OCR-0001', Data: { invoiceNumber: '24112000000098761234' } },
    durationMs: 1260,
    retryCount: 0,
    billable: true,
    costCents: 15,
    invoiceId: mockInvoiceId('inv-008'),
    bizType: 'INVOICE',
    bizId: mockInvoiceId('inv-008'),
    errorStack: null,
    traceId: 'demo-trace-0003',
    calledAt: iso(0, 9, 30),
  },
  {
    ...base('pl-4', 1),
    provider: ProviderCode.TPLUS,
    mode: ProviderMode.REAL,
    operation: 'PushBillToPool',
    status: ProviderCallStatus.QUOTA_EXCEEDED,
    resultCode: null,
    resultMessage: '租户本月 T+ 调用配额已用尽（500/500）',
    requestPayload: { invoiceNo: '24112000000098761234', accountSet: '演示科技-默认账套' },
    responsePayload: null,
    durationMs: 0,
    retryCount: 0,
    billable: false,
    costCents: 0,
    invoiceId: mockInvoiceId('inv-006'),
    bizType: 'INVOICE',
    bizId: mockInvoiceId('inv-006'),
    errorStack: null,
    traceId: 'demo-trace-0004',
    calledAt: iso(1, 16, 20),
  },
];

export const mockOperationLogs: OperationLog[] = [
  {
    ...base('ol-1', 0),
    userId: 'u-reviewer',
    userName: '周初审',
    action: 'invoice.review.reject',
    bizType: 'INVOICE',
    bizId: mockInvoiceId('inv-005'),
    before: { invoiceStatus: 'PENDING_REVIEW' },
    after: { invoiceStatus: 'REJECTED', reason: '供应商名称与合同不一致，请补充说明' },
    ip: '10.20.3.31',
    userAgent: 'Chrome/124',
    traceId: 'demo-trace-1001',
    createdAt: iso(1, 11, 5),
  },
  {
    ...base('ol-2', 0),
    userId: 'u-handler',
    userName: '孙经办',
    action: 'invoice.ocr.update',
    bizType: 'INVOICE',
    bizId: mockInvoiceId('inv-005'),
    before: { sellerTaxNumber: '9137O100MA3D9K8X2P' },
    after: { sellerTaxNumber: '91370100MA3D9K8X2P' },
    ip: '10.20.3.44',
    userAgent: 'Chrome/124',
    traceId: 'demo-trace-1002',
    createdAt: iso(1, 10, 20),
  },
];

export const mockTenantConfig: TenantConfig = {
  ...base('tc-config', 90),
  configs: {
    defaultAccountSet: '演示科技-默认账套',
    invoiceNoPrefix: 'BZ',
    autoSubmitAfterUpload: true,
    taskTimeoutHours: 24,
  },
  ocrFieldTemplate: {
    fields: ['invoiceCode', 'invoiceNumber', 'invoiceDate', 'sellerName', 'sellerTaxNumber', 'totalAmount'],
    requiredFields: ['invoiceNumber', 'invoiceDate', 'sellerName', 'sellerTaxNumber', 'totalAmount'],
    minConfidence: 85,
  },
  notifyRules: {
    taskTimeoutHours: 24,
    timeoutChannels: ['IN_APP', 'DINGTALK'],
    notifyRisk: true,
    riskNotifyMinLevel: RiskLevel.MEDIUM,
    notifyProviderError: true,
  },
};

export const mockMessages: Message[] = [
  {
    ...base('msg-1', 0),
    userId: 'u-reviewer',
    type: 'TODO',
    title: '您有 3 张发票待财务初审',
    content: '其中 1 张为高风险（已冲红），请优先处理。',
    bizType: 'INVOICE',
    bizId: mockInvoiceId('inv-003'),
    link: '/business/review',
    channels: ['IN_APP', 'DINGTALK'],
    deliveryResult: { IN_APP: 'ok', DINGTALK: 'ok' },
    isRead: false,
    readAt: null,
  },
  {
    ...base('msg-2', 0),
    userId: 'u-reviewer',
    type: 'RISK_ALERT',
    title: '高风险发票提醒：24112000000098760077 已冲红',
    content: '该发票默认禁止流转 T+，如需入账请走特例放行并填写专项审批依据。',
    bizType: 'INVOICE',
    bizId: mockInvoiceId('inv-003'),
    link: `/business/invoices/${mockInvoiceId('inv-003')}`,
    channels: ['IN_APP'],
    deliveryResult: { IN_APP: 'ok' },
    isRead: false,
    readAt: null,
  },
  {
    ...base('msg-3', 1),
    userId: 'u-tech',
    type: 'PROVIDER_ERROR',
    title: '企查查接口连续 2 次超时',
    content: '已按降级策略标记「外部校验暂不可用」，请检查网络与配额。',
    bizType: 'PROVIDER',
    bizId: 'QCC',
    link: '/admin/provider-logs',
    channels: ['IN_APP', 'SMS'],
    deliveryResult: { IN_APP: 'ok', SMS: 'ok' },
    isRead: true,
    readAt: iso(1, 9, 30),
  },
  {
    ...base('msg-4', 2),
    userId: 'u-handler',
    type: 'TIMEOUT',
    title: '发票 BZ20260820001234 已在您处停留超过 24 小时',
    content: '请尽快处理退回事项，超时任务会同步提醒部门负责人。',
    bizType: 'INVOICE',
    bizId: mockInvoiceId('inv-005'),
    link: `/business/invoices/${mockInvoiceId('inv-005')}`,
    channels: ['IN_APP', 'DINGTALK'],
    deliveryResult: { IN_APP: 'ok', DINGTALK: 'ok' },
    isRead: true,
    readAt: iso(2, 10, 0),
  },
];

export function mockTenantMetrics(invoiceCounts: Record<InvoiceStatus, number>): TenantMetrics {
  return {
    myPendingTasks: 3,
    invoiceByStatus: invoiceCounts,
    riskDistribution: {
      [RiskLevel.HIGH]: 1,
      [RiskLevel.MEDIUM]: 2,
      [RiskLevel.LOW]: 2,
      [RiskLevel.NONE]: 3,
    },
    quotaUsage: [
      { provider: ProviderCode.OCR, used: 486, limit: 2000 },
      { provider: ProviderCode.TAX_VERIFY, used: 412, limit: 1000 },
      { provider: ProviderCode.QCC, used: 388, limit: 1000 },
      { provider: ProviderCode.TPLUS, used: 500, limit: 500 },
      { provider: ProviderCode.DINGTALK, used: 620, limit: 5000 },
    ],
  };
}

/* ==========================================================================
 * 平台运营端
 * ========================================================================== */

export const mockTenants: TenantListItem[] = [
  {
    ...base('t-demo', 120),
    name: '演示科技有限公司',
    creditCode: '91370100MA3D9K8X2P',
    code: DEMO_TENANT_CODE,
    status: TenantStatus.ACTIVE,
    isolationMode: IsolationMode.SHARED,
    isolationTarget: null,
    contactName: '张管理',
    contactPhone: '13800000001',
    contactEmail: 'admin@demo.example.com',
    verifiedAt: iso(118, 10, 0),
    verifiedBy: '平台运营',
    suspendedReason: null,
    adminUsername: 'admin',
    remark: '演示租户',
    planName: '标准版',
    planTier: PlanTier.STANDARD,
    invoiceCountThisMonth: 128,
    quotaHealth: 0.62,
    userCount: 6,
  },
  {
    ...base('t-2', 90),
    name: '某某建筑工程有限公司',
    creditCode: '91370200MA3J4K5L6M',
    code: 'BLD002',
    status: TenantStatus.PENDING_VERIFY,
    isolationMode: IsolationMode.SHARED,
    isolationTarget: null,
    contactName: '刘经理',
    contactPhone: '13900000002',
    contactEmail: 'liu@example.com',
    verifiedAt: null,
    verifiedBy: null,
    suspendedReason: null,
    adminUsername: 'liuadmin',
    remark: '营业执照待人工核对',
    planName: '入门版',
    planTier: PlanTier.STARTER,
    invoiceCountThisMonth: 0,
    quotaHealth: 0,
    userCount: 1,
  },
  {
    ...base('t-3', 200),
    name: '恒通物流集团股份有限公司',
    creditCode: '91370300MA3N7P8Q9R',
    code: 'LOG003',
    status: TenantStatus.ACTIVE,
    isolationMode: IsolationMode.SCHEMA,
    isolationTarget: 'tenant_log003',
    contactName: '陈主管',
    contactPhone: '13700000003',
    contactEmail: 'chen@example.com',
    verifiedAt: iso(198, 10, 0),
    verifiedBy: '平台运营',
    suspendedReason: null,
    adminUsername: 'htadmin',
    remark: '已升级独立 Schema',
    planName: '专业版',
    planTier: PlanTier.PROFESSIONAL,
    invoiceCountThisMonth: 1264,
    quotaHealth: 0.94,
    userCount: 42,
  },
  {
    ...base('t-4', 60),
    name: '欠费停用示例企业',
    creditCode: '91370400MA3S1T2U3V',
    code: 'SUS004',
    status: TenantStatus.SUSPENDED,
    isolationMode: IsolationMode.SHARED,
    isolationTarget: null,
    contactName: '王先生',
    contactPhone: '13600000004',
    contactEmail: null,
    verifiedAt: iso(58, 10, 0),
    verifiedBy: '平台运营',
    suspendedReason: '连续 2 个计费周期欠费，数据保留 90 天',
    adminUsername: 'wangadmin',
    remark: null,
    planName: '入门版',
    planTier: PlanTier.STARTER,
    invoiceCountThisMonth: 0,
    quotaHealth: 0,
    userCount: 3,
  },
];

export const mockPlans: Plan[] = [
  {
    ...base('p-starter', 200),
    tier: PlanTier.STARTER,
    name: '入门版',
    priceCents: 199_00,
    billingMode: BillingMode.SUBSCRIPTION,
    periodDays: 30,
    maxUsers: 10,
    maxInvoicesPerMonth: 500,
    isolationMode: IsolationMode.SHARED,
    modules: { ocr: true, qcc: false, taxVerify: true, tplus: false, dingtalk: false },
    description: '小微企业，共享库、基础 OCR、有限调用量',
    isActive: true,
  },
  {
    ...base('p-standard', 200),
    tier: PlanTier.STANDARD,
    name: '标准版',
    priceCents: 899_00,
    billingMode: BillingMode.SUBSCRIPTION,
    periodDays: 30,
    maxUsers: 50,
    maxInvoicesPerMonth: 3000,
    isolationMode: IsolationMode.SCHEMA,
    modules: { ocr: true, qcc: true, taxVerify: true, tplus: true, dingtalk: true },
    description: '中型企业，独立 Schema、完整校验、T+ 对接',
    isActive: true,
  },
  {
    ...base('p-pro', 200),
    tier: PlanTier.PROFESSIONAL,
    name: '专业版',
    priceCents: 2_499_00,
    billingMode: BillingMode.SUBSCRIPTION,
    periodDays: 30,
    maxUsers: 200,
    maxInvoicesPerMonth: 20_000,
    isolationMode: IsolationMode.DATABASE,
    modules: { ocr: true, qcc: true, taxVerify: true, tplus: true, dingtalk: true },
    description: '大型企业，独立库、专属流程、专属接口配额',
    isActive: true,
  },
  {
    ...base('p-ent', 200),
    tier: PlanTier.ENTERPRISE,
    name: '定制版',
    priceCents: 0,
    billingMode: BillingMode.MODULE,
    periodDays: 365,
    maxUsers: 9999,
    maxInvoicesPerMonth: 999_999,
    isolationMode: IsolationMode.DATABASE,
    modules: { ocr: true, qcc: true, taxVerify: true, tplus: true, dingtalk: true },
    description: '集团客户，私有化部署、定制开发、SLA（价格另议）',
    isActive: true,
  },
];

export function mockPlatformMetrics(): PlatformMetrics {
  return {
    tenantCount: 4,
    activeTenantCount: 2,
    invoiceCountThisMonth: 1392,
    providerStats: [
      { provider: ProviderCode.OCR, totalCalls: 1392, successCalls: 1379, successRate: 0.99, avgDurationMs: 1180, totalCostCents: 20_880 },
      { provider: ProviderCode.TAX_VERIFY, totalCalls: 1284, successCalls: 1236, successRate: 0.96, avgDurationMs: 1460, totalCostCents: 38_520 },
      { provider: ProviderCode.QCC, totalCalls: 1180, successCalls: 1104, successRate: 0.94, avgDurationMs: 920, totalCostCents: 17_700 },
      { provider: ProviderCode.TPLUS, totalCalls: 640, successCalls: 622, successRate: 0.97, avgDurationMs: 780, totalCostCents: 0 },
      { provider: ProviderCode.DINGTALK, totalCalls: 2380, successCalls: 2371, successRate: 0.996, avgDurationMs: 210, totalCostCents: 0 },
    ],
  };
}

export const mockAuditLogs: AuditLog[] = [
  {
    ...base('al-1', 0),
    category: 'CROSS_TENANT_ACCESS',
    principalType: PrincipalType.PLATFORM,
    principalId: 'pu-1',
    principalName: '平台超级管理员',
    tenantId: DEMO_TENANT_ID,
    action: 'platform.tenant.detail.view',
    target: '演示科技有限公司',
    detail: { reason: '处理租户工单 #20260912-003' },
    ip: '10.0.0.8',
    traceId: 'demo-trace-2001',
  },
  {
    ...base('al-2', 0),
    category: 'CONFIG_CHANGE',
    principalType: PrincipalType.TENANT,
    principalId: 'u-tech',
    principalName: '陈技术',
    tenantId: DEMO_TENANT_ID,
    action: 'provider.config.update',
    target: 'TAX_VERIFY.mode: SANDBOX → REAL',
    detail: { before: { mode: 'SANDBOX' }, after: { mode: 'REAL' } },
    ip: '10.20.3.51',
    traceId: 'demo-trace-2002',
  },
  {
    ...base('al-3', 1),
    category: 'DATA_EXPORT',
    principalType: PrincipalType.TENANT,
    principalId: 'u-reviewer',
    principalName: '周初审',
    tenantId: DEMO_TENANT_ID,
    action: 'archive.export',
    target: '2026-08 归档数据（128 条）',
    detail: { format: 'xlsx', rows: 128 },
    ip: '10.20.3.31',
    traceId: 'demo-trace-2003',
  },
  {
    ...base('al-4', 3),
    category: 'PRIVILEGE_VIOLATION',
    principalType: PrincipalType.TENANT,
    principalId: 'u-handler',
    principalName: '孙经办',
    tenantId: DEMO_TENANT_ID,
    action: 'invoice.review.attempt',
    target: '发票 BZ20260820001234',
    detail: { message: '缺少 invoice:review 权限，已拒绝并告警' },
    ip: '10.20.3.44',
    traceId: 'demo-trace-2004',
  },
];

/* ==========================================================================
 * 演示账号表：**已从源码里删除**
 *
 * 原先这里是一张 DEMO_ACCOUNTS 表（租户标识 / 账号 / 密码），登录页拿它
 * 渲染一排"点击填入"的快捷按钮。问题不在方便，而在**口令随前端一起打包
 * 发到浏览器**：任何打开过登录页的人都能拿到各角色的密码，而演示环境与线上
 * 用的是同一份代码。
 *
 * 需要演示账号时，从演示环境的数据里现取（见部署指南的初始化说明），
 * 不要写回源码。
 * ========================================================================== */

/** 演示用的登录上下文（不依赖后端时的兜底，实际登录成功时以后端返回为准） */
export function mockAuthContext(username: string): AuthContext {
  const user = mockUsers.find((u) => u.username === username) ?? mockUsers[0]!;
  const roleCode = user.roles?.[0]?.code ?? DefaultRoleCode.TENANT_ADMIN;
  const roleDef = mockRoles.find((r) => r.code === roleCode);
  return {
    userId: user.id,
    username: user.username,
    name: user.name,
    principalType: PrincipalType.TENANT,
    tenantId: DEMO_TENANT_ID,
    tenantName: '演示科技有限公司',
    deptId: user.deptId,
    platformRole: null,
    roles: user.roles ?? [],
    permissions: [],
    dataScope: roleDef?.dataScope ?? DataScope.TENANT,
    customDeptIds: [],
    amountLimitCents: roleDef?.amountLimitCents ?? null,
    maxRiskLevel: roleDef?.maxRiskLevel ?? null,
  };
}

export { RiskItems };
